import React, { useState } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { Location, Group } from "@types";
import { useAppDispatch } from "@hooks/redux";
import { deleteApiary } from "@store/slices/locationsSlice";
import HiveCard from "../hives/HiveCard";
import ApiaryContextMenu from "./ApiaryContextMenu";

interface ApiaryPreviewProps {
  apiary: Location | Group;
  isGroup?: boolean;
  viewSize?: 'xs' | 'm' | 'xl';
  sharedWith?: string[];
}

const ApiaryPreview: React.FC<ApiaryPreviewProps> = ({
  apiary,
  viewSize = 'm',
  sharedWith,
}) => {
  const dispatch = useAppDispatch();

  const [expanded, setExpanded] = useState(true);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleDelete = async () => {
    await dispatch(deleteApiary(apiary.id));
  };

  const hiveCount = apiary.hives?.length || 0;

  // Calculate grid columns based on view size
  const getGridProps = () => {
    switch (viewSize) {
      case 'xs':
        return { xs: 4, sm: 3, md: 2, lg: 1.5 };
      case 'xl':
        return { xs: 12, sm: 6, md: 4, lg: 3 };
      case 'm':
      default:
        return { xs: 6, sm: 4, md: 3, lg: 2.4 }; // 5 items per row on large screens
    }
  };

  const gridProps = getGridProps();

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header - SmartNyuki style */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          py: 0.75,
          px: 0,
          mb: 1.5,
          borderBottom: "2px solid #F8B133", // Yellow underline
          cursor: "pointer",
        }}
        onClick={handleExpandClick}
      >
        {/* Circular Icon */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            backgroundColor: "#F8B133", // Yellow background
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 1,
          }}
        >
          {/* Using a simple Home icon to represent the apiary/house */}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
          </svg>
        </Box>

        <Box sx={{ flex: 1, mr: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "0.95rem", sm: "1.05rem" },
              color: "#F8B133",
              lineHeight: 1.2,
            }}
          >
            {apiary.name}
          </Typography>
          {sharedWith && sharedWith.length > 0 && (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", mt: 0.25 }}
            >
              Shared with {sharedWith.join(", ")}
            </Typography>
          )}
        </Box>

        {/* Expand/Collapse Icon */}
        <ExpandMoreIcon
          sx={{
            color: "#F8B133",
            fontSize: "1.5rem",
            transition: 'transform 0.3s ease',
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            mr: 1
          }}
        />

        {/* Settings/Context Menu - Grey Gear */}
        <Box onClick={(e) => e.stopPropagation()}>
          {apiary.owner && (
            <ApiaryContextMenu
              apiaryId={apiary.id}
              apiaryName={apiary.name}
              onDelete={handleDelete}
              isOwner={apiary.owner}
            />
          )}
        </Box>
      </Box>

      {/* Hives Grid - Only show when expanded */}
      {expanded && (
        <Box sx={{ px: 0, py: 0 }}>
          {hiveCount > 0 ? (
            <Grid container spacing={2}>
              {apiary.hives.map((hive) => (
                <Grid item {...gridProps} key={hive.id}>
                  <HiveCard hive={hive} viewSize={viewSize} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                color: "text.secondary",
                backgroundColor: "#fff",
                borderRadius: 1,
                border: "1px dashed #e0e0e0",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No hives in this apiary yet
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 1 }}
              >
                Click the + button to add your first hive
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ApiaryPreview;
